import { PlusIcon } from "@heroicons/react/outline";
import { CurlyBracesIcon } from "lucide-react";
import { observer } from "mobx-react-lite";

import { Button } from "../ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";

import ComplexSchemaEditorBanner from "./ComplexSchemaEditorBanner";
import JinjaHelpTooltip from "./JinjaHelpTooltip";
import SchemaMetadata from "./SchemaMetadata";
import SchemaParameter from "./SchemaParameter";

import { useSchemaEditor } from ".";

import EmptyPlaceholder from "@/components/ui/EmptyPlaceholder";

const AdvancedJsonOnlyPanel = observer(() => {
  const schemaEditorStore = useSchemaEditor();

  return (
    <div className="flex flex-col gap-4">
      <ComplexSchemaEditorBanner variant="unavailable" />
      {schemaEditorStore.schemaError ? (
        <p className="text-sm text-red-600 dark:text-red-400">{schemaEditorStore.schemaError}</p>
      ) : null}
    </div>
  );
});

const NonStandardSchemaView = ({
  hideMetadata = false,
  fullWidth = false,
}: {
  hideMetadata?: boolean;
  fullWidth?: boolean;
}) => (
  <div
    className={`flex w-full flex-1 flex-col gap-y-6 p-4 ${fullWidth ? "" : "mx-auto max-w-[560px]"}`}
  >
    {!hideMetadata && <SchemaMetadata />}
    <AdvancedJsonOnlyPanel />
  </div>
);

const Definitions = observer(() => {
  const schemaEditorStore = useSchemaEditor();
  const defs = schemaEditorStore.$defs;

  if (!defs || defs.length === 0) {
    return (
      <EmptyPlaceholder
        message="No definitions available"
        icon={<CurlyBracesIcon className="h-5 w-5 text-gray-400" />}
      />
    );
  }

  return (
    <div className="flex flex-col">
      {defs.map((def, index) => (
        <SchemaParameter
          // eslint-disable-next-line react/no-array-index-key
          key={`def-${index}`}
          parameter={def}
        />
      ))}
    </div>
  );
});

const AddDefinition = () => {
  const schemaEditorStore = useSchemaEditor();
  if (schemaEditorStore.isReadonly) {
    return null;
  }
  const handleAdd = () => {
    schemaEditorStore.addDefinition();
  };
  return (
    <Button className="mt-3" variant="secondaryOutline" onClick={handleAdd}>
      <PlusIcon className="mr-1 h-3 w-3" /> Add Definition
    </Button>
  );
};

const InteractiveEditor = ({
  onConfirmFormat,
  hideMetadata = false,
  fullWidth = false,
}: {
  onConfirmFormat: () => void;
  hideMetadata?: boolean;
  fullWidth?: boolean;
}) => {
  const schemaEditorStore = useSchemaEditor();
  const isDisabled = !schemaEditorStore.isStandardSchema;

  if (isDisabled) {
    return <NonStandardSchemaView hideMetadata={hideMetadata} fullWidth={fullWidth} />;
  }

  const parameters = (
    <>
      {(schemaEditorStore.schemaParameters.length > 0 &&
        schemaEditorStore.schemaParameters.map((parameter, index) => (
          <SchemaParameter
            // eslint-disable-next-line react/no-array-index-key
            key={index}
            parameter={parameter}
          />
        ))) || (
        <EmptyPlaceholder
          message="No parameters defined yet"
          icon={<CurlyBracesIcon className="h-5 w-5 text-gray-400" />}
        />
      )}
    </>
  );

  return (
    <div className="flex h-full min-h-0 flex-1 flex-col gap-y-6 overflow-auto">
      <div className="relative w-full pb-12">
        {!hideMetadata && <SchemaMetadata />}
        <ComplexSchemaEditorBanner />
        <div className="mt-6 w-full">
          <div className="mb-2 flex items-center">
            <Tabs defaultValue="parameters" className="w-full">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <TabsList>
                    <TabsTrigger value="parameters" className="flex items-center gap-2">
                      <h4 className="text-sm font-medium">Schema Parameters</h4>
                    </TabsTrigger>
                    <TabsTrigger value="definitions">Definitions</TabsTrigger>
                  </TabsList>
                  <JinjaHelpTooltip onConfirmFormat={onConfirmFormat} />
                </div>
              </div>
              <TabsContent value="parameters" className="w-full">
                <div className="overflow-x-auto px-1">{parameters}</div>
                {!schemaEditorStore.isReadonly && (
                  <Button
                    className="mt-3"
                    variant="secondaryOutline"
                    onClick={() => {
                      schemaEditorStore.addParameter();
                    }}
                  >
                    <PlusIcon className="mr-1 h-3 w-3" /> Add Parameter
                  </Button>
                )}
              </TabsContent>
              <TabsContent value="definitions">
                <div className="overflow-x-auto px-1">
                  <Definitions />
                  <AddDefinition />
                </div>
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </div>
    </div>
  );
};

export default observer(InteractiveEditor);
